/**
 * Ordinary kriging over scattered measurements.
 *
 * Knows nothing about soil, maps, or Panorama: it takes points with values in a
 * projected plane and predicts the value at other points in that plane. Feed it
 * degrees and it will treat a degree of longitude as a degree of latitude, so
 * callers project first.
 */

export type KrigingSample = { x: number; y: number; value: number }

/**
 * Shape of the assumed spatial correlation. All three rise from the nugget at
 * zero distance to the sill at the range; they differ in how abruptly, which is
 * the part a fit chooses between.
 */
export enum VariogramModel {
  Spherical = 'spherical',
  Exponential = 'exponential',
  Gaussian = 'gaussian'
}

export type Variogram = {
  model: VariogramModel
  /** Semivariance at zero distance — measurement error and variation finer than the sampling. */
  nugget: number
  /** Semivariance where samples stop being related. */
  sill: number
  /** Distance at which that happens. */
  range: number
}

const MODEL_SHAPE: Record<VariogramModel, (ratio: number) => number> = {
  [VariogramModel.Spherical]: (ratio) =>
    ratio >= 1 ? 1 : 1.5 * ratio - 0.5 * ratio * ratio * ratio,
  [VariogramModel.Exponential]: (ratio) => 1 - Math.exp(-3 * ratio),
  [VariogramModel.Gaussian]: (ratio) => 1 - Math.exp(-3 * ratio * ratio)
}

/** Semivariance the model predicts at a separation. */
export const semivariance = (variogram: Variogram, distance: number): number => {
  if (distance <= 0) return 0
  const shape = MODEL_SHAPE[variogram.model]
  const ratio = variogram.range <= 0 ? 1 : distance / variogram.range
  return variogram.nugget + (variogram.sill - variogram.nugget) * shape(ratio)
}

/** Lag bins in the experimental variogram. Enough to see a shape without emptying the bins. */
const LAG_BIN_COUNT = 12

/** Candidate ranges tried during the fit, as fractions of the sampled extent. */
const RANGE_CANDIDATE_COUNT = 40

/**
 * How much of the variance a fit may attribute to noise before it is rejected.
 *
 * One outlying reading inflates the semivariance of every pair it belongs to, and
 * an unconstrained fit answers that by calling almost everything noise. Kriging
 * under such a fit weights every sample about equally and predicts close to the
 * mean everywhere, which is a field of one color. Skew in a soil panel is
 * ordinary — one hot spot among low readings — so a fit that leaves this little
 * for distance to explain is treated the same as one that leaves none.
 */
const NUGGET_SILL_LIMIT = 0.5

/**
 * Fits a variogram to the samples by scanning candidate ranges.
 *
 * For a fixed range the model is linear in nugget and sill, so each candidate is
 * an exact two-parameter least squares fit and only the range needs searching.
 * That avoids a nonlinear optimizer and the local minima that come with one.
 *
 * Pairs beyond half the sampled extent are dropped, the usual practice: the far
 * bins hold few pairs and are dominated by whatever trend the data has, which
 * ordinary kriging does not model.
 */
export const fitVariogram = (
  samples: readonly KrigingSample[],
  model: VariogramModel = VariogramModel.Spherical
): Variogram | null => {
  const bins = experimentalVariogram(samples)
  if (bins.length < 2) return null

  const maxLag = Math.max(...bins.map(({ distance }) => distance))
  if (maxLag <= 0) return null

  // No pair is closer than the samples are to each other, so the experimental
  // variogram says nothing about ranges below that spacing. Fitting one anyway
  // lets the scan pick an arbitrarily short range, which makes every sample
  // uncorrelated with everywhere else and collapses the surface to islands.
  const shortestMeaningfulRange = Math.min(maxLag, medianNearestNeighborDistance(samples))

  const withNugget = bestFitOverRanges({
    bins,
    model,
    shortestMeaningfulRange,
    maxLag,
    fit: fitNuggetAndSill
  })

  // Readings that vary weakly in space still vary, so a fit that explains them
  // mostly as noise is refitted assuming the variation is spatial rather than
  // accepted as nothing to draw.
  if (withNugget !== null && explainsEnoughSpatially(withNugget)) return withNugget

  return bestFitOverRanges({
    bins,
    model,
    shortestMeaningfulRange,
    maxLag,
    fit: fitSillOnly
  })
}

/** Whether a fit leaves enough for distance to explain to be worth predicting from. */
const explainsEnoughSpatially = ({ nugget, sill }: Variogram): boolean =>
  sill > 0 && nugget / sill < NUGGET_SILL_LIMIT

const bestFitOverRanges = ({
  bins,
  model,
  shortestMeaningfulRange,
  maxLag,
  fit
}: {
  bins: readonly VariogramBin[]
  model: VariogramModel
  shortestMeaningfulRange: number
  maxLag: number
  fit: (params: {
    bins: readonly VariogramBin[]
    model: VariogramModel
    range: number
  }) => Variogram | null
}): Variogram | null => {
  let best: { variogram: Variogram; error: number } | null = null
  const span = maxLag - shortestMeaningfulRange

  for (let step = 0; step <= RANGE_CANDIDATE_COUNT; step++) {
    const range = shortestMeaningfulRange + (span * step) / RANGE_CANDIDATE_COUNT
    const fitted = fit({ bins, model, range })
    if (fitted === null) continue

    const error = bins.reduce((sum, bin) => {
      const residual = bin.semivariance - semivariance(fitted, bin.distance)
      return sum + residual * residual
    }, 0)

    if (best === null || error < best.error) best = { variogram: fitted, error }
  }

  return best?.variogram ?? null
}

type VariogramBin = { distance: number; semivariance: number; pairCount: number }

const experimentalVariogram = (samples: readonly KrigingSample[]): VariogramBin[] => {
  const pairs: { distance: number; semivariance: number }[] = []
  let maxDistance = 0

  for (let i = 0; i < samples.length; i++) {
    for (let j = i + 1; j < samples.length; j++) {
      const distance = separation(samples[i], samples[j])
      const difference = samples[i].value - samples[j].value
      pairs.push({ distance, semivariance: 0.5 * difference * difference })
      maxDistance = Math.max(maxDistance, distance)
    }
  }

  const cutoff = maxDistance / 2
  if (cutoff <= 0) return []

  const binWidth = cutoff / LAG_BIN_COUNT
  const totals = Array.from({ length: LAG_BIN_COUNT }, () => ({ sum: 0, count: 0 }))

  for (const pair of pairs) {
    if (pair.distance > cutoff || pair.distance <= 0) continue
    const index = Math.min(LAG_BIN_COUNT - 1, Math.floor(pair.distance / binWidth))
    totals[index].sum += pair.semivariance
    totals[index].count += 1
  }

  return totals.flatMap((total, index) =>
    total.count === 0
      ? []
      : [
          {
            distance: (index + 0.5) * binWidth,
            semivariance: total.sum / total.count,
            pairCount: total.count
          }
        ]
  )
}

/**
 * Least squares nugget and sill for a fixed range, weighted by pair count so a
 * bin built from three pairs does not outweigh one built from ninety.
 */
const fitNuggetAndSill = ({
  bins,
  model,
  range
}: {
  bins: readonly VariogramBin[]
  model: VariogramModel
  range: number
}): Variogram | null => {
  const shape = MODEL_SHAPE[model]

  // Regressing semivariance on the model shape: y = nugget * (1 - s) + sill * s
  let sumAA = 0
  let sumAB = 0
  let sumBB = 0
  let sumAY = 0
  let sumBY = 0

  for (const bin of bins) {
    const s = shape(range <= 0 ? 1 : bin.distance / range)
    const a = 1 - s
    const weight = bin.pairCount
    sumAA += weight * a * a
    sumAB += weight * a * s
    sumBB += weight * s * s
    sumAY += weight * a * bin.semivariance
    sumBY += weight * s * bin.semivariance
  }

  const determinant = sumAA * sumBB - sumAB * sumAB
  if (Math.abs(determinant) < Number.EPSILON) return null

  const nugget = (sumAY * sumBB - sumBY * sumAB) / determinant
  const sill = (sumBY * sumAA - sumAY * sumAB) / determinant

  // A negative nugget or a sill below it is a fit the data does not support;
  // clamping keeps the kriging system positive-definite.
  const clampedNugget = Math.max(0, nugget)
  return { model, nugget: clampedNugget, sill: Math.max(clampedNugget, sill), range }
}

/**
 * Least squares sill for a fixed range with the nugget pinned to zero, which
 * makes kriging an exact interpolator: the surface passes through every reading.
 */
const fitSillOnly = ({
  bins,
  model,
  range
}: {
  bins: readonly VariogramBin[]
  model: VariogramModel
  range: number
}): Variogram | null => {
  const shape = MODEL_SHAPE[model]

  let sumSS = 0
  let sumSY = 0

  for (const bin of bins) {
    const s = shape(range <= 0 ? 1 : bin.distance / range)
    sumSS += bin.pairCount * s * s
    sumSY += bin.pairCount * s * bin.semivariance
  }

  if (sumSS < Number.EPSILON) return null

  const sill = sumSY / sumSS
  return sill <= 0 ? null : { model, nugget: 0, sill, range }
}

/**
 * Builds a predictor for the samples under a variogram.
 *
 * The kriging matrix depends only on where the samples are, so it is factorized
 * once here and reused for every prediction. That is what makes interpolating
 * onto a grid of thousands of cells affordable; solving from scratch per cell
 * would not be.
 *
 * `null` when the samples cannot support a prediction — none at all, or a
 * configuration whose matrix is singular.
 */
export const krigingPredictor = ({
  samples,
  variogram
}: {
  samples: readonly KrigingSample[]
  variogram: Variogram
}): ((point: { x: number; y: number }) => number) | null => {
  const distinct = averageCoincident(samples)
  if (distinct.length === 0) return null
  if (distinct.length === 1) {
    const only = distinct[0].value
    return () => only
  }

  const size = distinct.length + 1
  const matrix = Array.from({ length: size }, (_, row) =>
    Array.from({ length: size }, (_, column) => {
      if (row === distinct.length && column === distinct.length) return 0
      if (row === distinct.length || column === distinct.length) return 1
      return semivariance(variogram, separation(distinct[row], distinct[column]))
    })
  )

  const factored = luDecompose(matrix)
  if (factored === null) return null

  return (point) => {
    const rightHandSide = [
      ...distinct.map((sample) => semivariance(variogram, separation(sample, point))),
      1
    ]
    const weights = luSolve(factored, rightHandSide)
    return distinct.reduce((total, sample, index) => total + weights[index] * sample.value, 0)
  }
}

const separation = (a: { x: number; y: number }, b: { x: number; y: number }): number =>
  Math.hypot(a.x - b.x, a.y - b.y)

/**
 * How far apart the samples are, taken as the median distance from each to its
 * closest neighbour. The median rather than the mean so a pair of near-duplicate
 * readings does not report the whole set as tightly spaced.
 */
const medianNearestNeighborDistance = (samples: readonly KrigingSample[]): number => {
  const nearest = samples.flatMap((sample, index) => {
    const distances = samples.flatMap((other, otherIndex) =>
      otherIndex === index ? [] : [separation(sample, other)]
    )
    const closest = Math.min(...distances.filter((distance) => distance > 0))
    return Number.isFinite(closest) ? [closest] : []
  })

  if (nearest.length === 0) return 0

  const sorted = nearest.toSorted((a, b) => a - b)
  return sorted[Math.floor(sorted.length / 2)]
}

/**
 * Collapses samples sharing a location into their mean.
 *
 * Two readings at one spot make the kriging matrix singular — they produce
 * identical rows — and a lab re-running a sample is an ordinary reason for that
 * to happen.
 */
const averageCoincident = (samples: readonly KrigingSample[]): KrigingSample[] => {
  const byLocation = new Map<string, { x: number; y: number; total: number; count: number }>()

  for (const sample of samples) {
    const key = `${sample.x},${sample.y}`
    const existing = byLocation.get(key)
    if (existing === undefined) {
      byLocation.set(key, { x: sample.x, y: sample.y, total: sample.value, count: 1 })
      continue
    }
    existing.total += sample.value
    existing.count += 1
  }

  return [...byLocation.values()].map(({ x, y, total, count }) => ({
    x,
    y,
    value: total / count
  }))
}

type Factorization = { rows: number[][]; pivots: number[] }

/** LU with partial pivoting. `null` when the matrix is singular to working precision. */
const luDecompose = (matrix: number[][]): Factorization | null => {
  const rows = matrix.map((row) => [...row])
  const size = rows.length
  const pivots = Array.from({ length: size }, (_, index) => index)

  for (let column = 0; column < size; column++) {
    let pivotRow = column
    for (let row = column + 1; row < size; row++) {
      if (Math.abs(rows[row][column]) > Math.abs(rows[pivotRow][column])) pivotRow = row
    }

    if (Math.abs(rows[pivotRow][column]) < 1e-12) return null

    if (pivotRow !== column) {
      const swapped = rows[column]
      rows[column] = rows[pivotRow]
      rows[pivotRow] = swapped
      const swappedPivot = pivots[column]
      pivots[column] = pivots[pivotRow]
      pivots[pivotRow] = swappedPivot
    }

    for (let row = column + 1; row < size; row++) {
      const factor = rows[row][column] / rows[column][column]
      rows[row][column] = factor
      for (let next = column + 1; next < size; next++) {
        rows[row][next] -= factor * rows[column][next]
      }
    }
  }

  return { rows, pivots }
}

const luSolve = ({ rows, pivots }: Factorization, rightHandSide: readonly number[]): number[] => {
  const size = rows.length
  const permuted = pivots.map((source) => rightHandSide[source])

  for (let row = 1; row < size; row++) {
    for (let column = 0; column < row; column++) {
      permuted[row] -= rows[row][column] * permuted[column]
    }
  }

  for (let row = size - 1; row >= 0; row--) {
    for (let column = row + 1; column < size; column++) {
      permuted[row] -= rows[row][column] * permuted[column]
    }
    permuted[row] /= rows[row][row]
  }

  return permuted
}
